import { useContext } from "react";

import { SendOutlined } from "@ant-design/icons";

import { UserContext } from "App/contexts";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { TOP_LEVEL_PAGES_TITLE_MAP } from "App/routes/pageNames";
import { AppPagePaths } from "App/routes/paths";
// Deliberately not importing from the "Components/shared/SendEventModal" barrel — it re-exports
// the full SendEventModal (CodeMirror, GraphQL mutation, etc.), which we don't want pulled into
// the globally-mounted Spotlight Search bundle just for this string constant.
import { SEND_EVENT_QUERY_PARAM } from "Components/shared/SendEventModal/sendEventQueryParam";
import {
    AI_AGENTS_SUB_PAGE_TITLES,
    AI_AGENTS_PAGE_DESCRIPTIONS,
    AI_AGENTS_TITLES_PATCH,
} from "Pages/agentBuilder/agentBuilderMetadata";
import { AIAgentPaths, aiEditPath } from "Pages/agentBuilder/Pages/paths";
import { AGENT_DESKTOP_PAGE_DESCRIPTIONS } from "Pages/agentDesktop/agentDesktopMetaData";
import { AgentDesktopPaths } from "Pages/agentDesktop/paths";
import {
    AUDIENCE_PAGE_DESCRIPTIONS,
    AUDIENCE_SUB_NAV_TITLES,
    AUDIENCE_SUB_PAGE_TITLES,
} from "Pages/audience/audienceMetadata";
import { audiencePathResources } from "Pages/audience/Pages/audiencePaths";
import { useAudiencePathing } from "Pages/audience/useAudiencePathing";
import { CONVO_INTEL_PAGE_DESCRIPTIONS, CONVO_INTEL_SUB_NAV_TITLES } from "Pages/convoIntel/convoIntelMetadata";
import { convoIntelPathResources } from "Pages/convoIntel/convoIntelPaths";
import {
    LIVE_OPS_PAGE_DESCRIPTIONS,
    LIVE_OPS_SPOTLIGHT_DESCRIPTIONS,
    LIVE_OPS_SPOTLIGHT_TITLES,
    LIVE_OPS_SUB_NAV_TITLES,
} from "Pages/liveOps/liveopsMetadata";
import { liveOpsPathResources } from "Pages/liveOps/Pages/liveOpsPaths";
import { taskPathResources } from "Pages/liveOps/Pages/TasksPage/Pages/taskPaths";
import { useLiveOpsPathing } from "Pages/liveOps/useLiveOpsPathing";
import {
    ORCHESTRATION_PAGE_DESCRIPTIONS,
    ORCHESTRATION_SPOTLIGHT_TITLES,
    ORCHESTRATION_SUB_PAGE_TITLES,
    ORCHESTRATION_SUB_NAV_TITLES,
} from "Pages/orchestration/orchestrationMetaData";
import { orchestrationPathResources } from "Pages/orchestration/Pages/orchestrationPaths";
import { RoutingRulesPaths } from "Pages/orchestration/Pages/taskRouting/Pages/paths";
import { useOrchestrationPathing } from "Pages/orchestration/useOrchestrationPathing";
import { REGAL_LIVE_PAGE_DESCRIPTIONS } from "Pages/regal-live/regalLiveMetadata";
import { REPORTING_PAGE_DESCRIPTIONS } from "Pages/reporting/reportingMetadata";
import { ADSettingsPaths } from "Pages/settings/Pages/AgentDesktop/paths";
import { AgentSettingsPaths } from "Pages/settings/Pages/AgentSettings/Pages/paths";
import { ComplianceSettingsPaths } from "Pages/settings/Pages/ComplianceSettings/paths";
import {
    DATA_MANAGEMENT_PAGE_DESCRIPTIONS,
    DATA_MANAGEMENT_SPOTLIGHT_TITLES,
} from "Pages/settings/Pages/DataManagement/dataManagementMetadata";
import { SIPPaths } from "Pages/settings/Pages/sip/paths";
import { TrustPaths } from "Pages/settings/Pages/Trust/paths";
import {
    USER_MANAGEMENT_PAGE_DESCRIPTIONS,
    USER_MANAGEMENT_SUB_PAGE_TITLES,
} from "Pages/settings/Pages/UserManagement/Pages/userManagementMetadata";
import { SettingsPaths } from "Pages/settings/paths";
import { SETTINGS_NAV_TITLES, SETTINGS_PAGE_DESCRIPTIONS } from "Pages/settings/settingsMetadata";
import { userHasRoles } from "Services/AuthService";
import { useFlags } from "Services/FeatureFlagService";
import { UserRole } from "Types/Auth";

export type SpotlightRouteItem = {
    route: string;
    name: string;
    description: string;
    /** Overrides the default `UnorderedListOutlined` icon shown for this result. */
    icon?: React.ReactNode;
};

// ============================================================================
// Audience Nav Items
// ============================================================================

function useAudienceSpotlightItems(showSegments: boolean, showContacts: boolean): SpotlightRouteItem[] {
    const { segmentsBasePath, contactsBasePath } = useAudiencePathing();

    const items: SpotlightRouteItem[] = [];

    if (showSegments) {
        items.push(
            {
                route: segmentsBasePath,
                name: AUDIENCE_SUB_NAV_TITLES[audiencePathResources.segments],
                description: AUDIENCE_PAGE_DESCRIPTIONS[audiencePathResources.segments],
            },
            {
                route: `${segmentsBasePath}/new`,
                name: AUDIENCE_SUB_PAGE_TITLES[`${audiencePathResources.segments}/new`],
                description: AUDIENCE_PAGE_DESCRIPTIONS[`${audiencePathResources.segments}/new`],
            }
        );
    }

    if (showContacts) {
        items.push({
            route: contactsBasePath,
            name: AUDIENCE_SUB_NAV_TITLES[audiencePathResources.contacts],
            description: AUDIENCE_PAGE_DESCRIPTIONS[audiencePathResources.contacts],
        });
    }

    return items;
}

// ============================================================================
// Live Ops Nav Items
// ============================================================================

function useLiveOpsSpotlightItems(
    showTasks: boolean,
    showAgents: boolean,
    showQueues: boolean,
    showRecentActivity: boolean,
    showSendEvent: boolean
): SpotlightRouteItem[] {
    const { tasksBasePath, recentTasksBasePath, agentsBasePath, recentActivityBasePath, queuesBasePath } =
        useLiveOpsPathing();

    const items: SpotlightRouteItem[] = [];

    if (showTasks) {
        items.push(
            {
                route: tasksBasePath,
                name: LIVE_OPS_SUB_NAV_TITLES[taskPathResources.upcoming],
                description: LIVE_OPS_PAGE_DESCRIPTIONS[taskPathResources.upcoming],
            },
            {
                route: recentTasksBasePath,
                name: LIVE_OPS_SUB_NAV_TITLES[taskPathResources.past],
                description: LIVE_OPS_PAGE_DESCRIPTIONS[taskPathResources.past],
            }
        );
    }

    if (showAgents) {
        items.push({
            route: agentsBasePath,
            name: LIVE_OPS_SUB_NAV_TITLES[liveOpsPathResources.agents],
            description: LIVE_OPS_PAGE_DESCRIPTIONS[liveOpsPathResources.agents],
        });
    }

    if (showQueues) {
        items.push({
            route: queuesBasePath,
            name: LIVE_OPS_SPOTLIGHT_TITLES[liveOpsPathResources.queues],
            description: LIVE_OPS_SPOTLIGHT_DESCRIPTIONS[liveOpsPathResources.queues],
        });
    }

    if (showRecentActivity) {
        items.push({
            route: recentActivityBasePath,
            name: LIVE_OPS_SUB_NAV_TITLES[liveOpsPathResources.recentActivity],
            description: LIVE_OPS_PAGE_DESCRIPTIONS[liveOpsPathResources.recentActivity],
        });
    }

    // Not a page of its own — navigating here lands on Recent Activity with the
    // Send Event modal already open, via SendEventButton's SEND_EVENT_QUERY_PARAM handling.
    if (showRecentActivity && showSendEvent) {
        items.push({
            route: `${recentActivityBasePath}?${SEND_EVENT_QUERY_PARAM}=1`,
            name: LIVE_OPS_SPOTLIGHT_TITLES.sendEvent,
            description: LIVE_OPS_SPOTLIGHT_DESCRIPTIONS.sendEvent,
            icon: <SendOutlined />,
        });
    }

    return items;
}

// ============================================================================
// Orchestration Nav Items
// ============================================================================

function useOrchestrationSpotlightItems(
    showJourneys: boolean,
    showCampaigns: boolean,
    showIVR: boolean,
    showTaskRouting: boolean,
    showCustomFunctions: boolean
): SpotlightRouteItem[] {
    const {
        campaignsBasePath,
        journeysBasePath,
        ivrsBasePath,
        customFunctionsBasePath,
        queuesBasePath,
        routingRulesBasePath,
    } = useOrchestrationPathing();

    const routingBasePath = routingRulesBasePath.replace(/\/rules$/, "");
    const items: SpotlightRouteItem[] = [];

    if (showJourneys) {
        items.push(
            {
                route: journeysBasePath,
                name: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.journeys],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[orchestrationPathResources.journeys],
            },
            {
                route: `${journeysBasePath}/new`,
                name: ORCHESTRATION_SUB_PAGE_TITLES[`${orchestrationPathResources.journeys}/new`],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[`${orchestrationPathResources.journeys}/new`],
            }
        );
    }

    if (showCampaigns) {
        items.push(
            {
                route: campaignsBasePath,
                name: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.campaigns],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[orchestrationPathResources.campaigns],
            },
            {
                route: `${campaignsBasePath}/new`,
                name: ORCHESTRATION_SUB_PAGE_TITLES[`${orchestrationPathResources.campaigns}/new`],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[`${orchestrationPathResources.campaigns}/new`],
            }
        );
    }

    if (showIVR) {
        items.push(
            {
                route: ivrsBasePath,
                name: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.ivrs],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[orchestrationPathResources.ivrs],
            },
            {
                route: `${ivrsBasePath}/new`,
                name: ORCHESTRATION_SUB_PAGE_TITLES[`${orchestrationPathResources.ivrs}/new`],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[`${orchestrationPathResources.ivrs}/new`],
            }
        );
    }

    if (showTaskRouting) {
        items.push(
            {
                route: routingRulesBasePath,
                name: ORCHESTRATION_SUB_NAV_TITLES[RoutingRulesPaths.rules],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[RoutingRulesPaths.rules],
            },
            {
                route: queuesBasePath,
                name: ORCHESTRATION_SPOTLIGHT_TITLES[RoutingRulesPaths.queues],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[RoutingRulesPaths.queues],
            },
            {
                route: `${queuesBasePath}/new`,
                name: ORCHESTRATION_SUB_PAGE_TITLES[`${RoutingRulesPaths.queues}/new`],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[`${RoutingRulesPaths.queues}/new`],
            },
            {
                route: `${routingBasePath}/${RoutingRulesPaths.attributes}`,
                name: ORCHESTRATION_SUB_NAV_TITLES[RoutingRulesPaths.attributes],
                description: ORCHESTRATION_PAGE_DESCRIPTIONS[RoutingRulesPaths.attributes],
            }
        );
    }

    if (showCustomFunctions) {
        items.push({
            route: customFunctionsBasePath,
            name: ORCHESTRATION_SUB_NAV_TITLES[orchestrationPathResources.customFunctions],
            description: ORCHESTRATION_PAGE_DESCRIPTIONS[orchestrationPathResources.customFunctions],
        });
    }

    return items;
}

// ============================================================================
// Agent Builder (AI Agents) Nav Items
// ============================================================================

function useAgentBuilderSpotlightItems(
    showAIAgents: boolean,
    showCustomAgents: boolean,
    showKnowledgeBase: boolean
): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [];

    if (showAIAgents) {
        items.push({
            route: `${AppPagePaths.aiAgents}/${AIAgentPaths.templates}`,
            name: AI_AGENTS_TITLES_PATCH[AIAgentPaths.templates],
            description: AI_AGENTS_PAGE_DESCRIPTIONS[AIAgentPaths.templates],
        });
    }

    if (showCustomAgents) {
        items.push({
            route: `${AppPagePaths.aiAgents}/${AIAgentPaths.custom}`,
            name: AI_AGENTS_SUB_PAGE_TITLES[AIAgentPaths.custom],
            description: AI_AGENTS_PAGE_DESCRIPTIONS[AIAgentPaths.custom],
        });
    }

    if (showKnowledgeBase) {
        items.push(
            {
                route: `${AppPagePaths.aiAgents}/${AIAgentPaths.knowledgeBase}`,
                name: AI_AGENTS_SUB_PAGE_TITLES[AIAgentPaths.knowledgeBase],
                description: AI_AGENTS_PAGE_DESCRIPTIONS[AIAgentPaths.knowledgeBase],
            },
            {
                route: `${AppPagePaths.aiAgents}/${AIAgentPaths.knowledgeBase}/${aiEditPath}/new`,
                name: AI_AGENTS_SUB_PAGE_TITLES[`${AIAgentPaths.knowledgeBase}/${aiEditPath}/new`],
                description: AI_AGENTS_PAGE_DESCRIPTIONS[`${AIAgentPaths.knowledgeBase}/${aiEditPath}/new`],
            }
        );
    }

    return items;
}

// ============================================================================
// Conversation Intelligence Nav Items
// ============================================================================

function useConvoIntelSpotlightItems(
    showRecordings: boolean,
    showObservability: boolean,
    showMoments: boolean,
    showScorecards: boolean,
    showInsights: boolean
): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [];

    if (showRecordings) {
        items.push({
            route: `${AppPagePaths.convoIntel}/${convoIntelPathResources.recordings}`,
            name: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.recordings],
            description: CONVO_INTEL_PAGE_DESCRIPTIONS[convoIntelPathResources.recordings],
        });
    }

    if (showObservability) {
        items.push({
            route: `${AppPagePaths.convoIntel}/${convoIntelPathResources.observability}`,
            name: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.observability],
            description: CONVO_INTEL_PAGE_DESCRIPTIONS[convoIntelPathResources.observability],
        });
    }

    if (showMoments) {
        items.push({
            route: `${AppPagePaths.convoIntel}/${convoIntelPathResources.moments}`,
            name: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.moments],
            description: CONVO_INTEL_PAGE_DESCRIPTIONS[convoIntelPathResources.moments],
        });
    }

    items.push({
        route: `${AppPagePaths.convoIntel}/${convoIntelPathResources.customAnalysis}`,
        name: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.customAnalysis],
        description: CONVO_INTEL_PAGE_DESCRIPTIONS[convoIntelPathResources.customAnalysis],
    });

    if (showScorecards) {
        items.push({
            route: `${AppPagePaths.convoIntel}/${convoIntelPathResources.scorecards}`,
            name: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.scorecards],
            description: CONVO_INTEL_PAGE_DESCRIPTIONS[convoIntelPathResources.scorecards],
        });
    }

    if (showInsights) {
        items.push({
            route: `${AppPagePaths.convoIntel}/${convoIntelPathResources.insights}`,
            name: CONVO_INTEL_SUB_NAV_TITLES[convoIntelPathResources.insights],
            description: CONVO_INTEL_PAGE_DESCRIPTIONS[convoIntelPathResources.insights],
        });
    }

    return items;
}

// ============================================================================
// Settings Nav Items
// ============================================================================

function getSettingsTopLevelNavSpotlightItems(): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [
        {
            route: AppPagePaths.settings,
            name: SETTINGS_NAV_TITLES[SettingsPaths.generalSettings],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.generalSettings],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.phoneNumbers}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.phoneNumbers],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.phoneNumbers],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.externalTransferPhonebook}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.externalTransferPhonebook],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.externalTransferPhonebook],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.integrations}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.integrations],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.integrations],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.recordings}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.recordings],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.recordings],
        },
    ];

    items.push({
        route: `${AppPagePaths.settings}/${SettingsPaths.contactLists}`,
        name: SETTINGS_NAV_TITLES[SettingsPaths.contactLists],
        description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.contactLists],
    });

    return items;
}

function getSettingsUserManagementSpotlightItems(
    showUserManagement: boolean,
    showCustomUserAttributes: boolean
): SpotlightRouteItem[] {
    if (!showUserManagement) {
        return [];
    }

    const items: SpotlightRouteItem[] = [
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementPageUsers}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageUsers],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.userManagementPageUsers],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementNewUser}`,
            name: USER_MANAGEMENT_SUB_PAGE_TITLES[SettingsPaths.userManagementNewUser],
            description: USER_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.userManagementNewUser],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementPageTeams}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageTeams],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.userManagementPageTeams],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementTeam}`,
            name: USER_MANAGEMENT_SUB_PAGE_TITLES[SettingsPaths.userManagementTeam],
            description: USER_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.userManagementTeam],
        },
    ];

    items.push(
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementPageSkills}`,
            name: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageSkills],
            description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.userManagementPageSkills],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementSkillsNew}`,
            name: USER_MANAGEMENT_SUB_PAGE_TITLES[SettingsPaths.userManagementSkillsNew],
            description: USER_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.userManagementSkillsNew],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.userManagementSkillsEdit}`,
            name: USER_MANAGEMENT_SUB_PAGE_TITLES[SettingsPaths.userManagementSkillsEdit],
            description: USER_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.userManagementSkillsEdit],
        }
    );

    if (showCustomUserAttributes) {
        items.push(
            {
                route: `${AppPagePaths.settings}/${SettingsPaths.userManagementPageCustomAttributes}`,
                name: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageCustomAttributes],
                description: SETTINGS_PAGE_DESCRIPTIONS[SettingsPaths.userManagementPageCustomAttributes],
            },
            {
                route: `${AppPagePaths.settings}/${SettingsPaths.userManagementCustomAttributesNew}`,
                name: USER_MANAGEMENT_SUB_PAGE_TITLES[SettingsPaths.userManagementCustomAttributesNew],
                description: USER_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.userManagementCustomAttributesNew],
            },
            {
                route: `${AppPagePaths.settings}/${SettingsPaths.userManagementCustomAttributesEdit}`,
                name: USER_MANAGEMENT_SUB_PAGE_TITLES[SettingsPaths.userManagementCustomAttributesEdit],
                description: USER_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.userManagementCustomAttributesEdit],
            }
        );
    }

    return items;
}

function getSettingsComplianceSpotlightItems(): SpotlightRouteItem[] {
    return [
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.contactRestrictions}`,
            name: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.contactRestrictions],
            description: SETTINGS_PAGE_DESCRIPTIONS[ComplianceSettingsPaths.contactRestrictions],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.holidays}`,
            name: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.holidays],
            description: SETTINGS_PAGE_DESCRIPTIONS[ComplianceSettingsPaths.holidays],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.callLimits}`,
            name: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.callLimits],
            description: SETTINGS_PAGE_DESCRIPTIONS[ComplianceSettingsPaths.callLimits],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.stateOfEmergency}`,
            name: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.stateOfEmergency],
            description: SETTINGS_PAGE_DESCRIPTIONS[ComplianceSettingsPaths.stateOfEmergency],
        },
    ];
}

function getSettingsTrustSpotlightItems(
    showBusinessProfiles: boolean,
    showBrandedNumbers: boolean,
    showTrustOverview: boolean
): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [];

    if (showTrustOverview) {
        items.push({
            route: `${AppPagePaths.settings}/${SettingsPaths.trust}/${TrustPaths.trustOverview}`,
            name: SETTINGS_NAV_TITLES[TrustPaths.trustOverview],
            description: SETTINGS_PAGE_DESCRIPTIONS[TrustPaths.trustOverview],
        });
    }

    if (showBusinessProfiles) {
        items.push({
            route: `${AppPagePaths.settings}/${SettingsPaths.trust}/${TrustPaths.businessProfiles}`,
            name: SETTINGS_NAV_TITLES[TrustPaths.businessProfiles],
            description: SETTINGS_PAGE_DESCRIPTIONS[TrustPaths.businessProfiles],
        });
    }

    if (showBrandedNumbers) {
        items.push({
            route: `${AppPagePaths.settings}/${SettingsPaths.trust}/${TrustPaths.brandingAndSpam}`,
            name: SETTINGS_NAV_TITLES[TrustPaths.brandingAndSpam],
            description: SETTINGS_PAGE_DESCRIPTIONS[TrustPaths.brandingAndSpam],
        });
    }

    return items;
}

function getSettingsSIPSpotlightItems(showSip: boolean): SpotlightRouteItem[] {
    if (!showSip) {
        return [];
    }

    return [
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.sip}/${SIPPaths.sipDomains}`,
            name: SETTINGS_NAV_TITLES[SIPPaths.sipDomains],
            description: SETTINGS_PAGE_DESCRIPTIONS[SIPPaths.sipDomains],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.sip}/${SIPPaths.ipAccessControlLists}`,
            name: SETTINGS_NAV_TITLES[SIPPaths.ipAccessControlLists],
            description: SETTINGS_PAGE_DESCRIPTIONS[SIPPaths.ipAccessControlLists],
        },
    ];
}

function getSettingsDataManagementSpotlightItems(): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [];

    items.push({
        route: `${AppPagePaths.settings}/${SettingsPaths.eventManagement}`,
        name: DATA_MANAGEMENT_SPOTLIGHT_TITLES[SettingsPaths.eventManagement],
        description: DATA_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.eventManagement],
    });

    items.push({
        route: `${AppPagePaths.settings}/${SettingsPaths.objectTypes}`,
        name: DATA_MANAGEMENT_SPOTLIGHT_TITLES[SettingsPaths.objectTypes],
        description: DATA_MANAGEMENT_PAGE_DESCRIPTIONS[SettingsPaths.objectTypes],
    });

    return items;
}

function getSettingsAgentDesktopSpotlightItems(showAgentPermissions: boolean): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.taskPreferences}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.taskPreferences],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.taskPreferences],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.dispositions}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.dispositions],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.dispositions],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.tags}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.tags],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.tags],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.cancellationReasons}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.cancellationReasons],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.cancellationReasons],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.activityStatuses}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.activityStatuses],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.activityStatuses],
        },
        {
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.templates}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.templates],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.templates],
        },
    ];

    if (showAgentPermissions) {
        items.push({
            route: `${AppPagePaths.settings}/${SettingsPaths.agentDesktop}/${ADSettingsPaths.agentPermissions}`,
            name: SETTINGS_NAV_TITLES[ADSettingsPaths.agentPermissions],
            description: SETTINGS_PAGE_DESCRIPTIONS[ADSettingsPaths.agentPermissions],
        });
    }

    return items;
}

function getSettingsMySettingsSpotlightItems(
    showEmail: boolean,
    showPreRecordedVmDrops: boolean
): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [];

    if (showEmail) {
        items.push({
            route: `${AppPagePaths.settings}/${SettingsPaths.mySettings}/${AgentSettingsPaths.integrations}`,
            name: SETTINGS_NAV_TITLES[AgentSettingsPaths.integrations],
            description: SETTINGS_PAGE_DESCRIPTIONS[AgentSettingsPaths.integrations],
        });
    }

    if (showPreRecordedVmDrops) {
        items.push({
            route: `${AppPagePaths.settings}/${SettingsPaths.mySettings}/${AgentSettingsPaths.recordings}`,
            name: SETTINGS_NAV_TITLES[AgentSettingsPaths.recordings],
            description: SETTINGS_PAGE_DESCRIPTIONS[AgentSettingsPaths.recordings],
        });
    }

    return items;
}

function useSettingsSpotlightItems(
    showSettings: boolean,
    settingsPages: {
        showUserManagement: boolean;
        showSip: boolean;
        showAgentPermissions: boolean;
        showBusinessProfiles: boolean;
        showBrandedNumbers: boolean;
        showTrustOverview: boolean;
        showEmail: boolean;
        showPreRecordedVmDrops: boolean;
        showCustomUserAttributes: boolean;
    }
): SpotlightRouteItem[] {
    // If settings is not available at all, return empty array
    if (!showSettings) {
        return [];
    }

    const topLevelNavItems = getSettingsTopLevelNavSpotlightItems();
    const userManagementItems = getSettingsUserManagementSpotlightItems(
        settingsPages.showUserManagement,
        settingsPages.showCustomUserAttributes
    );
    const complianceItems = getSettingsComplianceSpotlightItems();
    const callBrandingItems = getSettingsTrustSpotlightItems(
        settingsPages.showBusinessProfiles,
        settingsPages.showBrandedNumbers,
        settingsPages.showTrustOverview
    );
    const sipItems = getSettingsSIPSpotlightItems(settingsPages.showSip);
    const dataManagementItems = getSettingsDataManagementSpotlightItems();
    const agentDesktopItems = getSettingsAgentDesktopSpotlightItems(settingsPages.showAgentPermissions);
    const mySettingsItems = getSettingsMySettingsSpotlightItems(
        settingsPages.showEmail,
        settingsPages.showPreRecordedVmDrops
    );

    return [
        ...topLevelNavItems,
        ...userManagementItems,
        ...complianceItems,
        ...callBrandingItems,
        ...sipItems,
        ...dataManagementItems,
        ...agentDesktopItems,
        ...mySettingsItems,
    ];
}

// ============================================================================
// Other Top-Level Nav Items
// ============================================================================

function useOtherSpotlightItems(
    showAgentDesktop: boolean,
    showReporting: boolean,
    showRegalLive: boolean,
    showRegalCopilot: boolean
): SpotlightRouteItem[] {
    const items: SpotlightRouteItem[] = [];

    if (showAgentDesktop) {
        items.push(
            {
                route: AppPagePaths.agentDesktop,
                name: TOP_LEVEL_PAGES_TITLE_MAP[AppPagePaths.agentDesktop],
                description: AGENT_DESKTOP_PAGE_DESCRIPTIONS[AppPagePaths.agentDesktop],
            },
            {
                route: `${AppPagePaths.agentDesktop}/${AgentDesktopPaths.contacts[0]}`,
                name: AUDIENCE_SUB_NAV_TITLES[audiencePathResources.contacts],
                description:
                    AGENT_DESKTOP_PAGE_DESCRIPTIONS[`${AppPagePaths.agentDesktop}/${AgentDesktopPaths.contacts[0]}`],
            }
        );
    }

    if (showReporting) {
        items.push({
            route: AppPagePaths.reporting,
            name: TOP_LEVEL_PAGES_TITLE_MAP[AppPagePaths.reporting],
            description: REPORTING_PAGE_DESCRIPTIONS[AppPagePaths.reporting],
        });
    }

    if (showRegalLive) {
        items.push({
            route: AppPagePaths.regalLive,
            name: TOP_LEVEL_PAGES_TITLE_MAP[AppPagePaths.regalLive],
            description: REGAL_LIVE_PAGE_DESCRIPTIONS[AppPagePaths.regalLive],
        });
    }

    if (showRegalCopilot) {
        items.push({
            route: AppPagePaths.copilot,
            name: TOP_LEVEL_PAGES_TITLE_MAP[AppPagePaths.copilot],
            description: "AI-powered workspace assistant for navigation, troubleshooting, and operational guidance.",
        });
    }

    return items;
}

// ============================================================================
// Main Hook - Combines All Spotlight Items
// ============================================================================

/**
 * Hook that generates spotlight search routes based on user permissions and feature availability.
 *
 * Organized by nav structure:
 * - Audience (Segments, Contacts)
 * - Live Ops (Tasks, Agents, Queues, Recent Activity)
 * - Orchestration (Journeys, Campaigns, IVR, Task Routing)
 * - Agent Builder (AI Agents, Templates, Knowledge Base)
 * - Conversation Intelligence (Recordings, Moments, Scorecards, Insights)
 * - Settings (All settings pages)
 * - Other (Agent Desktop, Reporting, Regal Live)
 */
export function useSpotlightSearchRoutes(): SpotlightRouteItem[] {
    const {
        showADToAgent,
        showADToAdmin,
        showReporting,
        showRegalLive,
        showRegalCopilot,
        showSettings,
        showAIAgents,
        audiencePages,
        liveOpsPages,
        orchestrationPages,
        aiAgentsPages,
        convoIntelPages,
        settingsPages,
    } = usePageAvailability();
    const { sendEventFeature } = useFlags();
    const { user } = useContext(UserContext);
    const isBrandAdmin = userHasRoles(user, [UserRole.ADMIN]);

    const audienceItems = useAudienceSpotlightItems(audiencePages.showSegments, audiencePages.showContacts);

    const liveOpsItems = useLiveOpsSpotlightItems(
        liveOpsPages.showTasks,
        liveOpsPages.showAgents,
        liveOpsPages.showQueues,
        liveOpsPages.showRecentActivity,
        sendEventFeature && isBrandAdmin
    );

    const orchestrationItems = useOrchestrationSpotlightItems(
        orchestrationPages.showJourneys,
        orchestrationPages.showCampaigns,
        orchestrationPages.showIVR,
        orchestrationPages.showTaskRouting,
        orchestrationPages.showCustomFunctions
    );

    const agentBuilderItems = useAgentBuilderSpotlightItems(
        showAIAgents,
        aiAgentsPages.showCustomAgents,
        aiAgentsPages.showKnowledgeBase
    );

    const convoIntelItems = useConvoIntelSpotlightItems(
        convoIntelPages.showRecordings,
        convoIntelPages.showObservability,
        convoIntelPages.showMoments,
        convoIntelPages.showScorecards,
        convoIntelPages.showInsights
    );

    const settingsItems = useSettingsSpotlightItems(showSettings, {
        showUserManagement: settingsPages.showUserManagement,
        showSip: settingsPages.showSip,
        showAgentPermissions: settingsPages.showAgentPermissions,
        showBusinessProfiles: settingsPages.showBusinessProfiles,
        showBrandedNumbers: settingsPages.showBrandedNumbers,
        showTrustOverview: settingsPages.showTrustOverview,
        showEmail: settingsPages.showEmail,
        showPreRecordedVmDrops: settingsPages.showPreRecordedVmDrops,
        showCustomUserAttributes: settingsPages.showCustomUserAttributes,
    });

    const otherItems = useOtherSpotlightItems(
        showADToAgent || showADToAdmin,
        showReporting,
        showRegalLive,
        showRegalCopilot
    );

    return [
        ...audienceItems,
        ...liveOpsItems,
        ...orchestrationItems,
        ...agentBuilderItems,
        ...convoIntelItems,
        ...settingsItems,
        ...otherItems,
    ];
}
